import { useEffect, useState } from "react";

/**
 * Pings /api/health (Cloudflare Pages Function) on mount and shows a tiny
 * status pill: green = "API connected", red = "API disconnected".
 *
 * Uses fetch with a relative URL so it works on both the Cloudflare Pages
 * deployment (same-origin /api/health) and the legacy FastAPI deployment
 * (which also exposes /api/health on the same host via ingress).
 */
export default function ApiStatusBadge() {
  const [status, setStatus] = useState("checking"); // checking | connected | disconnected

  useEffect(() => {
    let cancelled = false;
    fetch("/api/health", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((body) => {
        if (cancelled) return;
        setStatus(body && body.success === true ? "connected" : "disconnected");
      })
      .catch(() => {
        if (!cancelled) setStatus("disconnected");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const tone =
    status === "connected"
      ? { dot: "#2d7a4a", text: "text-[#114232]", border: "border-[#2d7a4a]/30", bg: "bg-[#2d7a4a]/8", label: "Backend connected" }
      : status === "disconnected"
        ? { dot: "#c15337", text: "text-[#c15337]", border: "border-[#c15337]/30", bg: "bg-[#c15337]/8", label: "Backend disconnected" }
        : { dot: "#d49a36", text: "text-[#8a6520]", border: "border-[#d49a36]/30", bg: "bg-[#d49a36]/8", label: "Checking backend…" };

  return (
    <span
      data-testid="api-status-badge"
      data-status={status}
      title={tone.label}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-semibold ${tone.border} ${tone.bg} ${tone.text}`}
    >
      <span
        aria-hidden
        className={status === "checking" ? "animate-pulse" : ""}
        style={{
          width: 7,
          height: 7,
          borderRadius: 999,
          backgroundColor: tone.dot,
          display: "inline-block",
        }}
      />
      {tone.label}
    </span>
  );
}
